import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { UpdateFormType, updateFormSchema } from '../../formSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem } from 'components/ui/form';
import { InputGs, SelectGs } from 'components/ui/Forms';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useErrorHandler } from 'Exceptions/ErrorHandler';
import { useNavigate, useParams } from 'react-router-dom';
import { Carrier } from 'contracts/Carriers';
import { getCompanies, getClients, getCarriers } from 'hooks/api/Users/Options';
import { Company } from 'contracts/Companies';
import { Button } from 'components/ui/ButtonGs';
import { notify } from 'services';
import { ActivityIndicator } from 'styles/components';
import { showUser } from 'hooks/api/Users/showUser';
import { User } from 'contracts/Users';
import MultipleSelector from 'components/ui/multi-select';
import { updateUser } from 'hooks/api/Users/updateUser';
import { unlockLogin } from 'hooks/api/Users/unlockLogin';
import { Client } from 'contracts/Client';
import { useAuth } from 'hooks';
import {
  CARRIER_ACCESS_ROLE_KEEP_CURRENT,
  CARRIER_ACCESS_ROLE_OPTIONS,
} from 'lib/viagens-controle-permissions';

interface SelectOption {
  name: string;
  value: string;
  label: string;
}
interface OptionsType {
  [key: string]: SelectOption[];
}
type UserTypes =
  | 'carrierMember'
  | 'warehouseMember'
  | 'admin'
  | 'clientMember'
  | 'planner'
  | 'consultMember';

type UserTypesWithAssociation =
  | 'carrierMember'
  | 'warehouseMember'
  | 'clientMember';

const userOptions = [
  { name: 'Usuário de transportadora', value: 'carrierMember' },
  { name: 'Usuário de armazém', value: 'warehouseMember' },
  { name: 'Usuário de cliente', value: 'clientMember' },
  { name: 'Planejador', value: 'planner' },
  { name: 'Usuário de consulta', value: 'consultMember' },
  { name: 'Administrador', value: 'admin' },
];

const fieldNameDict: Record<UserTypesWithAssociation, string> = {
  carrierMember: 'carrierId',
  warehouseMember: 'companyId',
  clientMember: 'clientId',
};

// Depois da troca de perfil, o usuário passa a pertencer a outra aba/listagem.
const routeDictionary: Record<UserTypes, string> = {
  carrierMember: 'transportadora',
  warehouseMember: 'armazem',
  clientMember: 'clientes',
  admin: '',
  planner: 'planejadores',
  consultMember: '',
};

// Uma troca de perfil pode remover o usuário da lista antiga e adicioná-lo na nova.
const userListQueryKeys = [
  'carrierMembers',
  'warehouseMembers',
  'clientMembers',
  'admins',
  'planners',
  'consultMembers',
];

const requiresAssociation = (
  userType: UserTypes
): userType is UserTypesWithAssociation => {
  return userType in fieldNameDict;
};

const UpdateUsuarioForm = () => {
  const { errorHandler } = useErrorHandler();
  const { userBelongsToAnyOf } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [userType, setUserType] = useState<UserTypes | null>(null);
  const isAdmin = userBelongsToAnyOf('admin');

  const { data: carriers } = useQuery<Carrier[]>({
    queryKey: 'carriers',
    queryFn: getCarriers,
    onError: errorHandler,
  });
  const { data: companies } = useQuery<Company[]>({
    queryKey: 'companies',
    queryFn: getCompanies,
    onError: errorHandler,
  });
  const { data: clients } = useQuery<Client[]>({
    queryKey: 'clients',
    queryFn: getClients,
    onError: errorHandler,
  });
  const { data: user, isLoading: userLoading } = useQuery<User>({
    // Evita reutilizar cache de outro usuário aberto na mesma tela de edição.
    queryKey: ['user', id],
    queryFn: () => showUser(id!),
    onError: errorHandler,
    enabled: !!id,
  });

  const form = useForm<UpdateFormType>({
    resolver: zodResolver(updateFormSchema),
  });

  const options: OptionsType = useMemo(() => {
    if (!carriers || !companies || !clients)
      return { carrierMember: [], warehouseMember: [], clientMember: [] };
    return {
      carrierMember: carriers.map((item) => ({
        name: item.tradeName,
        value: `${item.id}`,
        label: item.tradeName,
      })),
      warehouseMember: companies.map((item) => ({
        name: item.tradeName,
        value: `${item.id}`,
        label: item.tradeName,
      })),
      clientMember: clients.map((item) => ({
        name: item.tradeName,
        label: item.tradeName,
        value: `${item.id}`,
      })),
    };
  }, [companies, carriers, clients]);

  const mutation = useMutation(updateUser, {
    onSuccess: () => {
      // Recarrega os dados que podem ter mudado de aba por causa do novo perfil.
      queryClient.invalidateQueries(['user', id]);
      queryClient.invalidateQueries('user');
      userListQueryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey);
      });

      notify('success', 'Usuário atualizado com sucesso');
      const updatedUserType = userType ?? (user?.type as UserTypes | null);

      if (updatedUserType) {
        navigate(`/configuracoes/usuarios/${routeDictionary[updatedUserType]}`);
        return;
      }

      navigate(-1);
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  const unlockMutation = useMutation(unlockLogin, {
    onSuccess: () => {
      queryClient.invalidateQueries(['user', id]);
      notify('success', 'Bloqueio de login removido com sucesso');
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  const isLoginLocked =
    !!user &&
    ((user.failedLoginAttempts ?? 0) >= 3 ||
      (user.loginLockedUntil &&
        new Date(user.loginLockedUntil).getTime() > Date.now()));

  function handleSubmit(data: UpdateFormType) {
    if (userType && requiresAssociation(userType)) {
      const field = fieldNameDict[userType] as
        | 'carrierId'
        | 'companyId'
        | 'clientId';
      const formatData = {
        ...data,
        // O backend legado atualiza users.type a partir de user.type; type no topo só escolhe a rota.
        user: { ...data.user, type: data.type },
        [field]: data[field]?.map((item) => Number(item.value)),
      };
      if (!formatData.accessRoleName) {
        delete formatData.accessRoleName;
      }
      mutation.mutate({ data: formatData, id: id! });
    } else if (userType === 'planner') {
      const payload = {
        user: { name: data.user.name, email: data.user.email, type: data.type },
        material_id: data.material_id,
        type: data.type,
      };
      mutation.mutate({ data: payload, id: id! });
    } else if (userType === 'admin' || userType === 'consultMember') {
      const payload = {
        user: { name: data.user.name, email: data.user.email, type: data.type },
        type: data.type,
      };
      mutation.mutate({ data: payload, id: id! });
    }
  }

  useEffect(() => {
    if (user) {
      const userType = user.type as UserTypes;
      setUserType(userType);
      form.setValue('type', userType);
      form.setValue('user.name', user.name);
      form.setValue('user.email', user.email);

      if (requiresAssociation(userType)) {
        const fieldType = fieldNameDict[userType];
        form.setValue(
          fieldType as unknown as any,
          user[userType].map((item: any) => ({
            value: item.id,
            label: item.tradeName,
          }))
        );
      }

      if (userType === 'planner' && user.planner?.materials) {
        const materialIds = user.planner.materials
          .map((material: any) => material.id)
          .join(',');
        form.setValue('material_id', materialIds);
      }
    }
  }, [user, form]);

  return (
    <Form {...form}>
      <>
        {userLoading ? (
          <ActivityIndicator />
        ) : (
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-4 gap-4"
          >
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <SelectGs
                  {...field}
                  onValueChange={(e: UserTypes) => {
                    setUserType(e);
                    form.setValue('type', e);
                  }}
                  label="Tipo de usuário"
                  content={userOptions}
                />
              )}
            />
            <FormField
              control={form.control}
              name="user.name"
              render={({ field }) => (
                <InputGs {...field} label="Nome do usuário" />
              )}
            />
            <FormField
              control={form.control}
              name="user.email"
              render={({ field }) => <InputGs {...field} label="E-mail" />}
            />
            <InputGs disabled label="Senha" />
            {isAdmin && isLoginLocked ? (
              <div className="col-span-full rounded-md border border-orange-200 bg-orange-50 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-GilroySemibold text-sm text-slate-800">
                    Login bloqueado por tentativas inválidas
                  </p>
                  <p className="text-sm text-slate-600">
                    Este usuário está temporariamente impedido de entrar após
                    múltiplas falhas de autenticação.
                  </p>
                </div>
                <Button.Root
                  className="bg-orange-500 shrink-0"
                  size="sm"
                  type="button"
                  disabled={unlockMutation.isLoading}
                  onClick={() => user && unlockMutation.mutate(user.id)}
                >
                  {unlockMutation.isLoading ? (
                    <ActivityIndicator />
                  ) : (
                    'Desbloquear login'
                  )}
                </Button.Root>
              </div>
            ) : null}
            {userType === 'carrierMember' && (
              <FormField
                control={form.control}
                name="accessRoleName"
                render={({ field }) => (
                  <SelectGs
                    {...field}
                    value={field.value ?? CARRIER_ACCESS_ROLE_KEEP_CURRENT}
                    onValueChange={(value: string) => {
                      form.setValue(
                        'accessRoleName',
                        value === CARRIER_ACCESS_ROLE_KEEP_CURRENT
                          ? undefined
                          : (value as UpdateFormType['accessRoleName'])
                      );
                    }}
                    label="Perfil de acesso"
                    content={[
                      {
                        name: 'Manter perfil atual',
                        value: CARRIER_ACCESS_ROLE_KEEP_CURRENT,
                      },
                      ...CARRIER_ACCESS_ROLE_OPTIONS.map((option) => ({
                        name: option.label,
                        value: option.value,
                      })),
                    ]}
                  />
                )}
              />
            )}
            <div className="col-span-full">
              {userType && requiresAssociation(userType) && (
                <FormField
                  key={user?.id + '-' + userType}
                  control={form.control}
                  name={fieldNameDict[userType] as any}
                  render={({ field }) => (
                    <FormItem>
                      <label className="font-GilroySemibold text-sm text-slate-800">
                        Selecione...
                      </label>
                      <FormControl>
                        <MultipleSelector
                          {...field}
                          options={
                            options[userType] as unknown as {
                              label: string
                              value: string
                            }[]
                          }
                          inputProps={{ autoFocus: false, ref: field.ref }}
                          onChange={(e) =>
                            form.setValue(fieldNameDict[userType] as any, e)
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
              {userType === 'planner' && (
                <FormField
                  control={form.control}
                  name="material_id"
                  render={({ field }) => (
                    <InputGs
                      {...field}
                      label="Número do material"
                      placeholder="Digite o número do material"
                    />
                  )}
                />
              )}
            </div>
            <div className="col-span-4">
              <div className="flex gap-4 justify-end">
                <Button.Root
                  className="bg-orange-100 text-orange-600"
                  size="sm"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root className="bg-orange-500" size="sm" type="submit">
                  {mutation.isLoading ? <ActivityIndicator /> : 'Salvar'}
                </Button.Root>
              </div>
            </div>
          </form>
        )}
      </>
    </Form>
  );
};

export default UpdateUsuarioForm;
